import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  Avatar
} from '@mui/material';
import {
  Event as EventIcon,
  Star as StarIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import MKButton from '../MKButton';
import { PRIMARY_PINK } from '../../constants';

const FeaturedMajorCard = ({ major }) => {
  const navigate = useNavigate();

  const formatDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const startStr = start.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: start.getFullYear() !== end.getFullYear() ? 'numeric' : undefined
    });
    
    const endStr = end.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    
    return `${startStr} - ${endStr}`;
  };

  const handleMoreDetails = () => {
    navigate(`/majors/${major.id}`);
  };

  const handleViewClub = () => {
    if (major.club_handle) {
      navigate(`/clubs/${major.club_handle}`);
    }
  };

  return (
    <Card 
      sx={{ 
        maxWidth: 400, 
        minHeight: 400,
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
        }
      }}
    >
      {major.banner_url && (
        <CardMedia
          component="img"
          height="400"
          image={major.banner_url}
          alt={major.title}
          sx={{ 
            objectFit: 'contain',
            backgroundColor: '#f5f5f5',
            borderBottom: '1px solid #e0e0e0'
          }}
        />
      )}
      
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
          <Typography 
            variant="h6" 
            component="h3" 
            sx={{ 
              fontWeight: 600,
              color: PRIMARY_PINK,
              flexGrow: 1,
              mr: 1
            }}
          >
            {major.title}
          </Typography>
          {major.priority && major.priority > 5 && (
            <Chip
              icon={<StarIcon />}
              label="Featured"
              size="small"
              color="warning"
              sx={{ flexShrink: 0 }}
            />
          )}
        </Box>

        {/* Club Info */}
        {major.club_name && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {major.club_avatar_url && (
              <Avatar 
                src={major.club_avatar_url} 
                alt={major.club_name}
                sx={{ 
                  width: 24, 
                  height: 24, 
                  mr: 1,
                  '& img': {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '50%'
                  }
                }}
              />
            )}
            <Typography variant="body2" color="text.secondary">
              {major.club_name}
            </Typography>
          </Box>
        )}

        {/* Date Range */}
        {(major.start_date || major.end_date) && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
            <CalendarIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary', mt: 0.2 }} />
            <Typography variant="body2" color="text.secondary">
              {formatDateRange(major.start_date, major.end_date)}
            </Typography>
          </Box>
        )}

        {/* Content/Description */}
        {major.content && (
          <Box
            sx={{ 
              mb: 2,
              flexGrow: 1,
              maxHeight: '7.0em', // Approximately 6 lines (1.4 * 6)
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                whiteSpace: 'pre-line',
                lineHeight: 1.4
              }}
            >
              {major.content}
            </Typography>
            {/* Ellipsis at the end of visible content */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                background: 'inherit',
                paddingLeft: '0.5em',
                color: 'text.secondary',
                fontSize: '0.875rem'
              }}
            >
              ...
            </Box>
          </Box>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
          <MKButton
            variant="contained"
            color="primary"
            onClick={handleMoreDetails}
            fullWidth
            sx={{
              backgroundColor: PRIMARY_PINK,
              color: '#ffffff',
              '&:hover': {
                backgroundColor: PRIMARY_PINK,
                opacity: 0.9
              }
            }}
          >
            More Details
          </MKButton>
          {major.club_handle && (
            <MKButton
              variant="outlined"
              onClick={handleViewClub}
              fullWidth
              sx={{
                borderColor: PRIMARY_PINK,
                color: PRIMARY_PINK,
                '&:hover': {
                  borderColor: PRIMARY_PINK,
                  backgroundColor: 'rgba(233, 30, 99, 0.08)'
                }
              }}
            >
              View Club
            </MKButton>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default FeaturedMajorCard;

